import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, MapPin, TrendingUp, Shield, Star, ChevronDown, ChevronUp, Users, DollarSign, Award } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";

const TAX_PRO = "https://static.prod-images.emergentagent.com/jobs/e6a17b6d-85b8-4581-ad6d-24de2a75247c/images/905d630f165563e2ea24f07f455af8e70b6c9710b4ad26fb539861fba2231666.png";

const faqs = [
  { q: "What does 'claiming a ZIP code' mean?", a: "When you claim a ZIP code, you become the exclusive Verified Provider for that area. Taxpayers searching NextDayRefund.com in your ZIP will only see your business — no competitors." },
  { q: "How long does the exclusivity last?", a: "Your ZIP code claim is valid through the end of the 2027 tax season (April 15, 2027). Each new tax season will offer renewal opportunities." },
  { q: "What happens after I pay?", a: "Your listing goes live immediately. You receive a 'Verified Provider' badge to place on your website, and leads from your ZIP code are routed directly to you." },
  { q: "How many leads can I expect?", a: "It depends on your ZIP code's population density and search volume. High-demand areas like urban ZIP codes in high-unbanked states generate the most leads." },
  { q: "Can I claim multiple ZIP codes?", a: "Yes! You can claim as many ZIP codes as you want. Each is $150 for the season. The more you claim, the wider your exclusive territory." },
  { q: "Is there a refund policy?", a: "Due to the exclusivity model, payments are non-refundable once your ZIP code has been activated and your listing has gone live." },
];

export default function ForTaxPros() {
  const [openFaq, setOpenFaq] = useState(null);

  const features = [
    { icon: <MapPin size={24} />, title: "Exclusive ZIP Code Territory", desc: "Be the ONLY verified provider shown to taxpayers in your claimed ZIP code. Zero competition." },
    { icon: <Users size={24} />, title: "High-Intent Lead Routing", desc: "Taxpayers searching for next-day refund advances are sent directly to your business." },
    { icon: <Award size={24} />, title: "Verified Provider Badge", desc: "Display your authority badge on your website — builds trust and converts visitors." },
    { icon: <TrendingUp size={24} />, title: "2027 Demand is Massive", desc: "IRS processing delays continue to push taxpayers toward local next-day refund advance providers." },
    { icon: <Shield size={24} />, title: "Protected Listing", desc: "Your ZIP code exclusivity is enforced by our platform — competitors cannot buy into your area." },
    { icon: <DollarSign size={24} />, title: "Just $150 for the Season", desc: "One-time payment of $150 covers you through April 15, 2027. Pay once, own your market." },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="For Tax Professionals — Own Your ZIP Code Territory | NextDayRefund.com"
        description="Claim an exclusive ZIP code on NextDayRefund.com and be the only verified tax professional shown to taxpayers in your area. $150/year. 2027 tax season now open."
        canonical="/for-tax-pros"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "For Tax Professionals", url: "/for-tax-pros" },
        ]}
      />
      <Navbar />

      {/* Hero */}
      <section className="bg-[#003366] py-16" data-testid="tax-pros-hero">
        <div className="section-container">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="flex-1 text-white">
              <div className="text-xs font-bold uppercase tracking-wider text-[#2ECC71] mb-4">For Tax Professionals — 2027 Season</div>
              <h1 className="text-4xl sm:text-5xl font-black leading-tight mb-5">
                Own Your ZIP Code.<br />Own Your 2027 Market.
              </h1>
              <p className="text-slate-300 text-lg leading-relaxed mb-8 max-w-xl">
                The 2026 tax season just ended. The early movers for 2027 are claiming territory <strong className="text-white">right now</strong>. Don't let your competitors lock you out of your own market — secure your exclusive ZIP code at the early bird rate before prices increase.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/claim" className="btn-action" data-testid="hero-claim-btn">
                  Claim Your ZIP Code — $150
                </Link>
                <Link to="/register" className="btn-outline !border-white !text-white hover:!bg-white hover:!text-[#003366]" data-testid="hero-register-btn">
                  Create Account
                </Link>
              </div>
            </div>
            <div className="hidden md:block w-64 flex-shrink-0">
              <img src={TAX_PRO} alt="Tax Professional" className="rounded-xl shadow-2xl w-full" />
            </div>
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="py-16" data-testid="features-section">
        <div className="section-container">
          <div className="text-center mb-12">
            <div className="text-xs font-bold uppercase tracking-wider text-[#003366] mb-3">What You Get</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A]">Everything You Need to Win Your Market</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <div key={i} className="card-base" data-testid={`feature-card-${i}`}>
                <div className="w-12 h-12 bg-[#003366]/10 rounded-lg flex items-center justify-center text-[#003366] mb-4">
                  {f.icon}
                </div>
                <h3 className="font-bold text-[#0F172A] mb-2">{f.title}</h3>
                <p className="text-[#64748B] text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-[#F8FAFC] py-16 border-y border-slate-100" data-testid="pricing-section">
        <div className="section-container">
          <div className="max-w-lg mx-auto text-center">
            <div className="text-xs font-bold uppercase tracking-wider text-[#003366] mb-3">Simple Pricing</div>
            <div className="bg-white border-2 border-[#003366] rounded-xl p-8 shadow-lg">
              <div className="mb-2 text-[#64748B] text-sm uppercase tracking-wider font-semibold">2027 Season Early Bird Rate</div>
              <div className="text-5xl font-black text-[#003366] mb-1">$150</div>
              <div className="text-[#64748B] mb-2">per ZIP code · valid through April 15, 2027</div>
              <div className="text-xs text-[#2ECC71] font-bold mb-6">Lock in now before prices increase</div>
              <ul className="space-y-3 text-left mb-8">
                {[
                  "Exclusive ZIP code territory",
                  "Verified Provider listing on NextDayRefund.com",
                  "High-intent lead routing",
                  "Verified Provider badge for your website",
                  "Priority placement in search results",
                  "Valid through end of 2027 tax season",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-[#334155]">
                    <CheckCircle size={16} className="text-[#2ECC71] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/claim" className="btn-action w-full text-center block" data-testid="pricing-claim-btn">
                Claim Your ZIP Code Now
              </Link>
              <p className="text-xs text-[#64748B] mt-3">Secure payment via Stripe · One-time fee</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16" data-testid="faq-section">
        <div className="section-container max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#0F172A]">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-[#F8FAFC] transition-colors"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-semibold text-[#0F172A]">{f.q}</span>
                  {openFaq === i ? <ChevronUp size={18} className="text-[#003366]" /> : <ChevronDown size={18} className="text-[#64748B]" />}
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 text-[#334155] text-sm leading-relaxed border-t border-slate-100 pt-4">
                    {f.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#2ECC71] py-12" data-testid="final-cta-section">
        <div className="section-container text-center text-white">
          <Star size={32} className="mx-auto mb-4 text-white" />
          <h2 className="text-3xl font-black mb-3">Ready to Own Your Market?</h2>
          <p className="text-white/90 mb-6 max-w-md mx-auto">Zip codes are going fast. Don't let a competitor claim yours first.</p>
          <Link to="/claim" className="bg-[#003366] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#002244] transition-all inline-block shadow-lg" data-testid="footer-claim-btn">
            Claim Your ZIP Code — $150
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
